import { FC, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import "draft-js/dist/Draft.css";
import { useSearchParams } from "react-router-dom";
import {
  useGetPostCommentsQuery,
  useUpdatePostCommentMutation,
  useDeletePostCommentMutation,
  useLazyGetRepliesPostCommentsQuery,
} from "@/store/api/postCommentApi";
import DeleteModal from "@/components/UI/modal/DeleteModal/DeleteModal";
import ReportModal from "@/components/UI/modal/ReportModal/ReportModal";
import ReplyCommentModal from "@/components/UI/modal/ReplyCommentModal/ReplyCommentModal";
import EditCommentModal from "@/components/UI/modal/EditCommentModal/EditCommentModal";
import CommentThread from "./CommentThread";
import { useAppSelector } from "@/hooks/useAppSelector";
import { PendingComment } from "@/store/slice/commentPendingSlice";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { useDispatch } from "react-redux";
import { setCommentLoading } from "@/store/slice/loadingSlice";

type ApiComment = {
  uid: string;
  replies?: {
    data?: { uid: string }[];
  };
};

interface IPostCommentsProps {
  postId: string;
  replyUid: string;
  setReplyUid: (uid: string) => void;
  pendingText: string;
  setPendingText: (text: string) => void;
  editPost: editComment | null;
  setEditPost: (edit: editComment | null) => void;
  onCommentCreated: () => void;
  isLiked: boolean;
}

export type editComment = {
  uid: string;
  comment: string | undefined;
};

interface IOpenEditModal {
  open: boolean;
  uid: string;
  comment: string;
  parentComment?: {
    avatar: string;
    username: string;
    comment: string;
    createdAt: string;
  };
  avatar: string;
}

const PostComments: FC<IPostCommentsProps> = ({
  postId,
  setPendingText,
  setReplyUid,
  setEditPost,
  editPost,
  onCommentCreated,
  isLiked,
}) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const commentUidParam = searchParams.get("commentUid");
  const scrolledRef = useRef(false);
  const [fetchReplies] = useLazyGetRepliesPostCommentsQuery();
  const pendingComments = useAppSelector((state) => state.commentPending.items);
  const { data: comments, isLoading } = useGetPostCommentsQuery(postId);
  const { user } = useAppSelector((state) => state.auth);
  const [deletedComments, setDeletedComments] = useState<Set<string>>(new Set());
  const [updatePostComment] = useUpdatePostCommentMutation();
  const [deletePostComment] = useDeletePostCommentMutation();

  const [editedComments, setEditedComments] = useState<Record<string, string>>({});
  const { user: authUser } = useAppSelector((state) => state.auth);

  const [openReportModal, setOpenReportModal] = useState({
    open: false,
    uid: "",
  });

  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    uid: "",
  });

  const [openEditModal, setOpenEditModal] = useState<IOpenEditModal>({
    open: false,
    uid: "",
    comment: "",
    avatar: "",
  });

  const [openReplyModal, setOpenReplyModal] = useState<{
    open: boolean;
    comment: string;
    uid: string;
    handle: string;
    username: string;
    avatar: string;
    time: string;
    level: number;
  }>({
    open: false,
    comment: "",
    uid: "",
    handle: "",
    username: "",
    avatar: "",
    time: "",
    level: 0,
  });

  const [anchorEls, setAnchorEls] = useState<Record<string, HTMLElement | null>>({});

  const handleClick1 = (uid: string) => (event: MouseEvent<HTMLElement>) => {
    setAnchorEls((prev) => ({ ...prev, [uid]: event.currentTarget }));
  };

  const handleClose1 = (uid: string) => () => {
    setAnchorEls((prev) => ({ ...prev, [uid]: null }));
  };

  const handleReport = (uid: string) => {
    setOpenReportModal({ open: true, uid });
    setAnchorEls((prev) => ({ ...prev, [uid]: null }));
  };

  const handleDeleteClick = async (uid: string) => {
    setOpenDeleteModal({ open: true, uid });
    setAnchorEls((prev) => ({ ...prev, [uid]: null }));
  };

  const handleConfirmDelete = async () => {
    const uidToDelete = openDeleteModal.uid;
    if (!uidToDelete) return;

    dispatch(setCommentLoading(true));
    try {
      setDeletedComments((prev) => new Set(prev).add(uidToDelete));
      await deletePostComment(uidToDelete).unwrap();
      setOpenDeleteModal({ open: false, uid: "" });
    } catch (error) {
      console.error("Failed to delete comment", error);
      setDeletedComments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(uidToDelete);
        return newSet;
      });
    } finally {
      dispatch(setCommentLoading(false));
    }
  };

  const handleUpdate = async (uid: string, content: string) => {
    setEditedComments((prev) => ({ ...prev, [uid]: content }));

    try {
      await updatePostComment({ uid, content }).unwrap();
    } catch (error) {
      console.error("Failed to update comment", error);
      setEditedComments((prev) => {
        const newState = { ...prev };
        delete newState[uid];
        return newState;
      });
    }
  };

  const [openReplies, setOpenReplies] = useState<Record<string, boolean>>({});
  //@ts-ignore
  const [loadedReplies, setLoadedReplies] = useState<Record<string, any[]>>({});

  const toggleReplies = (uid: string) => {
    setOpenReplies((prev) => ({
      ...prev,
      [uid]: !prev[uid],
    }));
  };
  useEffect(() => {
    pendingComments.forEach((c) => {
      if (c.replyToUid) {
        setOpenReplies((prev) => ({
          ...prev,
          [c.replyToUid!]: true,
        }));
      }
    });
  }, [pendingComments]);

  const syncedReplyParentsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    pendingComments.forEach((c) => {
      if (!c.replyToUid || c.status !== "published") return;
      if (syncedReplyParentsRef.current.has(c.uid)) return;
      syncedReplyParentsRef.current.add(c.uid);
      const parentUid = c.replyToUid;
      fetchReplies({ repliesUid: parentUid, page: 1, limit: 15 })
        .unwrap()
        .then((res) => {
          const fetched = res.data || [];
          const enriched = fetched.map((r: any) => ({
            ...r,
            avatar: r.author?.image?.url || "",
          }));
          setLoadedReplies((prev) => ({ ...prev, [parentUid]: enriched }));
        })
        .catch(() => undefined);
    });
  }, [pendingComments, fetchReplies]);

  const scrollToComment = useCallback((uid: string) => {
    const tryScroll = (attempts = 0) => {
      const el = document.querySelector(`[data-comment-id="${uid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        (el as HTMLElement).style.transition = "background-color 0.3s";
        (el as HTMLElement).style.backgroundColor = "#E8F0FE";
        setTimeout(() => {
          (el as HTMLElement).style.backgroundColor = "transparent";
        }, 2000);
        const url = new URL(window.location.href);
        url.searchParams.delete("commentUid");
        window.history.replaceState({}, "", url.pathname + url.search);
      } else if (attempts < 20) {
        setTimeout(() => tryScroll(attempts + 1), 200);
      }
    };
    tryScroll();
  }, []);

  useEffect(() => {
    if (!commentUidParam || !comments || scrolledRef.current) return;
    scrolledRef.current = true;

    // Check if comment is top-level
    const isTopLevel = comments.some((c: any) => c.uid === commentUidParam);
    if (isTopLevel) {
      scrollToComment(commentUidParam);
      return;
    }

    // Check if it's in already-loaded replies
    for (const comment of comments) {
      const replies = comment.replies?.data || [];
      if (replies.some((r: any) => r.uid === commentUidParam)) {
        setOpenReplies((prev) => ({ ...prev, [comment.uid]: true }));
        scrollToComment(commentUidParam);
        return;
      }
    }

    // It's a deeper reply — need to fetch replies for each parent to find it
    const findAndExpandReply = async () => {
      for (const comment of comments) {
        if ((comment.repliesCount || 0) === 0) continue;
        try {
          const res = await fetchReplies({ repliesUid: comment.uid, page: 1, limit: 50 }).unwrap();
          const fetched = res.data || res || [];
          if (fetched.some((r: any) => r.uid === commentUidParam)) {
            const parentComment = res.meta?.parentComment;
            const enriched = fetched.map((reply: any) => ({
              ...reply,
              avatar: reply.author?.image?.url || "",
              parentComment: parentComment
                ? {
                    avatar: parentComment.author?.image?.url || "",
                    username: parentComment.author?.username || "",
                    comment: parentComment.content || "",
                    createdAt: parentComment.createdAt || "",
                  }
                : undefined,
            }));
            setLoadedReplies((prev) => ({ ...prev, [comment.uid]: enriched }));
            setOpenReplies((prev) => ({ ...prev, [comment.uid]: true }));
            scrollToComment(commentUidParam);
            return;
          }
        } catch {
          // continue searching
        }
      }
    };
    findAndExpandReply();
  }, [comments, commentUidParam]);

  const mergeComments = (apiComments: any[], pendingComments: PendingComment[]) => {
    const fakeComments = pendingComments
      .filter((c) => !c.replyToUid)
      .map((c) => ({
        uid: c.uid,
        content: c.text,
        author: {
          uid: authUser?.uid,
          username: authUser?.username,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        repliesCount: 0,
        reactionsCount: 0,
        replies: { data: [] },
        pending: c.status === "pending",
        error: c.status === "error",
        level: 0,
      }));

    const serverUids = new Set(apiComments.map((c) => c.uid));

    const visibleFake = fakeComments.filter((f) => !serverUids.has(f.uid));

    return [...visibleFake, ...apiComments];
  };
  const mergeReplies = (
    parentUid: string,
    serverReplies: any[] = [],
    pendingComments: PendingComment[],
    user: any
  ) => {
    const pendingReplies = pendingComments
      .filter((c) => c.replyToUid === parentUid)
      .map((c) => ({
        uid: c.uid,
        content: c.text,
        author: {
          uid: user?.uid,
          username: user?.username,
          name: user?.name,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        repliesCount: 0,
        reactionsCount: 0,
        replies: { data: [] },
        pending: c.status === "pending",
        error: c.status === "error",
        level: c.level,
      }));

    const serverUids = new Set(serverReplies.map((r) => r.uid));

    const visiblePending = pendingReplies.filter((r) => !serverUids.has(r.uid));

    return [...visiblePending, ...serverReplies];
  };

  useEffect(() => {
    if (!isLoading && comments) {
      setPendingText("");
    }
  }, [comments, isLoading]);

  const commentsWithFake = mergeComments(comments || [], pendingComments);
  const mergedRepliesByParent: Record<string, any[]> = {};
  commentsWithFake.forEach((comment) => {
    const serverReplies = comment.replies?.data ?? [];
    mergedRepliesByParent[comment.uid] = mergeReplies(
      comment.uid,
      serverReplies,
      pendingComments,
      user
    );
  });

  const combinedReplies: Record<string, any[]> = { ...loadedReplies, ...mergedRepliesByParent };
  const nestedPendingParents = new Set(
    pendingComments
      .filter((c) => c.replyToUid && !mergedRepliesByParent[c.replyToUid])
      .map((c) => c.replyToUid as string)
  );
  nestedPendingParents.forEach((parentUid) => {
    combinedReplies[parentUid] = mergeReplies(
      parentUid,
      loadedReplies[parentUid] ?? [],
      pendingComments,
      user
    );
  });

  const handleCloseReplyModal = () => {
    setOpenReplyModal({
      open: false,
      uid: "",
      comment: "",
      handle: "",
      username: "",
      avatar: "",
      time: "",
      level: 0,
    });
    setReplyUid("");
  };

  useEffect(() => {
    if (!comments) return;

    setLoadedReplies((prev) => {
      const next: Record<string, any[]> = { ...prev };

      comments.forEach((comment: ApiComment) => {
        const serverReplies = comment.replies?.data ?? [];

        next[comment.uid] = mergeReplies(comment.uid, serverReplies, pendingComments, user);
      });

      return next;
    });
  }, [comments, pendingComments, user]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          height: "20vh",
        }}
      >
        <CustomLoader />
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <Box sx={{ padding: "0 1.25rem 1.25rem 1.25rem" }} className="hide-scrollbar">
        {commentsWithFake.map((comment: any, index: number) => {
          const editedContent = editedComments[comment.uid];
          const isDeleted = deletedComments.has(comment.uid);
          const isLast = index === commentsWithFake.length - 1;

          return (
            <CommentThread
              key={comment.uid}
              comment={{
                ...comment,
                content: isDeleted ? "[deleted]" : (editedContent ?? comment.content),
              }}
              anchorEls={anchorEls}
              handleClick={handleClick1}
              handleClose={handleClose1}
              handleDelete={handleDeleteClick}
              handleReport={handleReport}
              setOpenEditModal={setOpenEditModal}
              setReplyUid={setReplyUid}
              setOpenReplyModal={setOpenReplyModal}
              setEdit={setEditPost}
              level={comment.level ?? 0}
              openReplies={openReplies}
              loadedReplies={combinedReplies}
              toggleReplies={toggleReplies}
              setLoadedReplies={setLoadedReplies}
              status={comment.pending ? "pending" : comment.error ? "error" : "published"}
              isLast={isLast}
            />
          );
        })}
      </Box>
      <>
        {openDeleteModal.open && (
          <DeleteModal
            open={openDeleteModal.open}
            handleClose={() => setOpenDeleteModal({ open: false, uid: "" })}
            onDelete={handleConfirmDelete}
            variant="comment"
            title="Are you sure you want to delete this comment?"
            sxTitle={{
              maxWidth: "299px",
            }}
          />
        )}

        {openReportModal.open && (
          <ReportModal
            open={openReportModal.open}
            handleClose={() => setOpenReportModal({ open: false, uid: "" })}
            postUid={openReportModal.uid}
            variant="comment"
          />
        )}
        {openReplyModal.open && (
          <ReplyCommentModal
            open={openReplyModal.open}
            handleClose={handleCloseReplyModal}
            openReplyModal={openReplyModal}
            uid={openReplyModal.uid}
            postId={postId}
            setLoadedReplies={setLoadedReplies}
            setOpenReplies={setOpenReplies}
            setReplyUid={setReplyUid}
            onCommentCreated={onCommentCreated}
            isLiked={isLiked}
          />
        )}
        {openEditModal.open && (
          <EditCommentModal
            open={openEditModal.open}
            handleClose={() =>
              setOpenEditModal({
                ...openEditModal,
                open: false,
              })
            }
            openEditModal={openEditModal}
            uid={openEditModal.uid}
            postId={postId}
            edit={editPost}
            handleUpdate={handleUpdate}
            parentComment={openEditModal.parentComment}
            avatar={openEditModal.avatar}
          />
        )}
      </>
    </Box>
  );
};

export default PostComments;
